import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { SharedModule } from '@shared/shared.module';
import { TranslocoService } from '@ngneat/transloco';
import { PlatformChartListItemDto } from '@data/_generated-api/platform-api/models/platform-chart-list-item-dto';
import { PlatformChartType } from '@data/_generated-api/platform-api/models/platform-chart-type';
import { DsQueryFilterOperator } from '@common/result/data-source/enum/ds-query-filter-operator';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdChartCalculationResult } from './hd-chart-library.models';
import { HdChartLibraryService } from './hd-chart-library.service';
import { buildCompositeChartPreset } from './hd-composite-chart.utils';

const T = 'humanDesign.chart.createDialog';

/**
 * Composiet-variant van het aanmaak-formulier: twee doorzoekbare dropdowns met
 * de Standard-charts uit de eigen bibliotheek. Berekenen haalt beide documenten
 * op en bouwt client-side het composite-document (zelfde combineer-laag als
 * cycle charts) — er is geen backend-berekening nodig.
 */
@Component({
  selector: 'hd-composite-create-form',
  standalone: true,
  imports: [FormsModule, NgSelectModule, SharedModule],
  templateUrl: './hd-composite-create-form.component.html',
  styleUrl: './hd-composite-create-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdCompositeCreateFormComponent {
  private readonly library = inject(HdChartLibraryService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** false: de host kan geen onopgeslagen chart tonen → vinkje "enkel berekenen" verbergen (blijft dan uit). */
  readonly calculateOnlyOffered = input(true);
  readonly calculated = output<HdChartCalculationResult>();
  readonly cancelled = output<void>();

  protected readonly charts = signal<PlatformChartListItemDto[]>([]);
  protected readonly chartsLoading = signal(false);
  protected readonly chart1Id = signal<string | null>(null);
  protected readonly chart2Id = signal<string | null>(null);
  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);
  /** "Enkel berekenen" aangevinkt → de pagina slaat niet automatisch op. */
  protected readonly calculateOnly = signal(false);

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(`${T}.${key}`, undefined, language);
    return {
      chart1Label: translate('compositeChart1Label'),
      chart2Label: translate('compositeChart2Label'),
      chartPlaceholder: translate('compositeChartPlaceholder'),
      sameChart: translate('compositeSameChart'),
      cancel: translate('cancel'),
      calculate: translate('calculate'),
      calculating: translate('calculating'),
      calculationError: translate('calculationError'),
      calculateOnlyLabel: translate('calculateOnlyLabel'),
      submit: this.calculateOnly() ? translate('calculate') : translate('calculateAndSave'),
    };
  });

  protected readonly sameChartChosen = computed(() =>
    this.chart1Id() != null && this.chart1Id() === this.chart2Id());

  protected get canSubmit(): boolean {
    return !this.loading()
      && this.chart1Id() != null
      && this.chart2Id() != null
      && !this.sameChartChosen();
  }

  constructor() {
    void this.loadCharts();
  }

  private async loadCharts(): Promise<void> {
    this.chartsLoading.set(true);
    try {
      const items = await this.library.queryAllCharts({
        sortOptions: [{ propertyName: 'Name', direction: 'ASC' }],
        filterOptionGroup: {
          logicalOperator: '',
          filterOptionGroups: [],
          filterOptions: [{
            propertyName: 'Type',
            operator: DsQueryFilterOperator.IntEquals,
            value: `${PlatformChartType.Standard}`,
          }],
        },
      });
      this.charts.set(items);
    } catch {
      this.charts.set([]);
    } finally {
      this.chartsLoading.set(false);
    }
  }

  protected async submit(): Promise<void> {
    const chart1Id = this.chart1Id();
    const chart2Id = this.chart2Id();
    if (chart1Id == null || chart2Id == null || !this.canSubmit) return;

    this.loading.set(true);
    this.error.set(null);
    try {
      const [chart1, chart2] = await Promise.all([
        this.library.getChartDocument(chart1Id),
        this.library.getChartDocument(chart2Id),
      ]);
      const preset = buildCompositeChartPreset({
        chart1Preset: chart1.preset,
        chart1Id,
        chart2Preset: chart2.preset,
        chart2Id,
      });
      this.calculated.emit({ preset, name: preset.name, calculateOnly: this.calculateOnly() });
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : this.text().calculationError);
    } finally {
      this.loading.set(false);
    }
  }
}
